import { Link } from "react-router-dom";
import { useAuth } from "@shared/contexts/auth-context";
import { Button } from "@shared/ui/components/button";
import { Card } from "@shared/ui/components/card";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@shared/ui/components/dialog";
import { Check, Zap } from "lucide-react";

interface UpgradeLimitModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function UpgradeLimitModal({ open, onOpenChange }: UpgradeLimitModalProps): JSX.Element {
  const { user } = useAuth();

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle className="text-2xl font-bold">Monthly Limit Reached</DialogTitle>
          <DialogDescription>
            You&apos;ve used all {user?.transcriptionsLimit} transcriptions this month. Upgrade to continue
            transcribing.
          </DialogDescription>
        </DialogHeader>

        <div className="grid gap-4 md:grid-cols-2 mt-4">
          <Card className="p-6 border-primary">
            <div className="mb-4">
              <h3 className="text-xl font-bold text-foreground mb-2">Basic Plan</h3>
              <div className="flex items-baseline gap-1 mb-3">
                <span className="text-3xl font-bold text-foreground">$19</span>
                <span className="text-muted-foreground">/month</span>
              </div>
            </div>

            <ul className="space-y-2 mb-6">
              <li className="flex items-start gap-2 text-sm">
                <Check className="h-4 w-4 text-primary flex-shrink-0 mt-0.5" />
                <span>50 transcriptions per month</span>
              </li>
              <li className="flex items-start gap-2 text-sm">
                <Check className="h-4 w-4 text-primary flex-shrink-0 mt-0.5" />
                <span>Up to 2 hours per file</span>
              </li>
              <li className="flex items-start gap-2 text-sm">
                <Check className="h-4 w-4 text-primary flex-shrink-0 mt-0.5" />
                <span>All export formats</span>
              </li>
              <li className="flex items-start gap-2 text-sm">
                <Check className="h-4 w-4 text-primary flex-shrink-0 mt-0.5" />
                <span>Speaker detection</span>
              </li>
            </ul>

            <Button asChild className="w-full">
              <Link to="/pricing">Upgrade to Basic</Link>
            </Button>
          </Card>

          <Card className="p-6 border-primary bg-primary text-white">
            <div className="flex items-center gap-2 mb-4">
              <Zap className="h-5 w-5" />
              <span className="text-sm font-semibold">BEST VALUE</span>
            </div>

            <div className="mb-4">
              <h3 className="text-xl font-bold mb-2">Premium Plan</h3>
              <div className="flex items-baseline gap-1 mb-3">
                <span className="text-3xl font-bold">$49</span>
                <span className="text-white/80">/month</span>
              </div>
            </div>

            <ul className="space-y-2 mb-6">
              <li className="flex items-start gap-2 text-sm">
                <Check className="h-4 w-4 flex-shrink-0 mt-0.5" />
                <span>Unlimited transcriptions</span>
              </li>
              <li className="flex items-start gap-2 text-sm">
                <Check className="h-4 w-4 flex-shrink-0 mt-0.5" />
                <span>Unlimited file length</span>
              </li>
              <li className="flex items-start gap-2 text-sm">
                <Check className="h-4 w-4 flex-shrink-0 mt-0.5" />
                <span>API access</span>
              </li>
              <li className="flex items-start gap-2 text-sm">
                <Check className="h-4 w-4 flex-shrink-0 mt-0.5" />
                <span>Priority support 24/7</span>
              </li>
            </ul>

            <Button asChild className="w-full bg-white text-primary hover:bg-white/90">
              <Link to="/pricing">Upgrade to Premium</Link>
            </Button>
          </Card>
        </div>
      </DialogContent>
    </Dialog>
  );
}
